'use client';

import { type FormEvent, useEffect, useState } from 'react';
import { ValidationError } from 'yup';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import {
  AUTH_LOGIN_ACCENT_CLASS,
  AUTH_LOGIN_STATUS_BANNER_CLASS,
} from './AuthSplitLayout';
import { emailSchema } from '../validation/sign-up';
import {
  getApiErrorMessage,
  useForgotPasswordMutation,
} from '../hooks/useAuthMutations';

export type ForgotPasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function ForgotPasswordDialog({
  open,
  onOpenChange,
}: ForgotPasswordDialogProps) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const forgotMutation = useForgotPasswordMutation();

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setEmailError(null);
    forgotMutation.reset();
  }, [open, forgotMutation.reset]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError(null);
    try {
      const trimmed = emailSchema.validateSync(email.trim(), {
        abortEarly: true,
      });
      forgotMutation.mutate(
        { email: normalizeEmail(trimmed) },
        {
          onSuccess: () => {
            setEmailError(null);
          },
        },
      );
    } catch (err) {
      if (err instanceof ValidationError) {
        setEmailError(err.message);
      }
    }
  };

  const apiError = forgotMutation.error
    ? getApiErrorMessage(forgotMutation.error)
    : null;

  const successMessage =
    forgotMutation.isSuccess && forgotMutation.data?.success
      ? (forgotMutation.data.data?.message ??
        forgotMutation.data.message ??
        'If an account exists for that email address, password reset instructions have been sent.')
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} appTheme='auth-login'>
      <DialogContent showCloseButton className='bg-white'>
        <DialogHeader className='gap-2 text-left'>
          <p className={AUTH_LOGIN_ACCENT_CLASS}>Password recovery</p>
          <DialogTitle className='font-heading text-2xl font-semibold tracking-tight text-foreground'>
            Reset your password
          </DialogTitle>
          <DialogDescription className='text-muted-foreground'>
            Enter the email you use to sign in. We will send reset instructions
            if an account exists for that address.
          </DialogDescription>
        </DialogHeader>

        {successMessage ? (
          <>
            <DialogBody>
              <p className={AUTH_LOGIN_STATUS_BANNER_CLASS} role='status'>
                {successMessage}
              </p>
            </DialogBody>
            <DialogFooter className='border-stone-200 bg-white'>
              <Button
                type='button'
                colorTone='primary'
                appearance='filled'
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className='flex min-h-0 flex-1 flex-col'
            onSubmit={handleSubmit}
          >
            <DialogBody className='space-y-4'>
              <div className='space-y-1.5'>
                <label
                  htmlFor='forgot-email'
                  className='text-sm font-medium text-foreground'
                >
                  Email
                </label>
                <Input
                  id='forgot-email'
                  type='email'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  autoComplete='email'
                  placeholder='example@email.com'
                  required
                  invalid={Boolean(emailError)}
                  aria-invalid={Boolean(emailError)}
                />
                {emailError ? (
                  <p className='text-sm text-red-600'>{emailError}</p>
                ) : null}
                {apiError ? (
                  <p className='text-sm text-red-600' role='alert'>
                    {apiError}
                  </p>
                ) : null}
              </div>
            </DialogBody>
            <DialogFooter className='border-stone-200 bg-white'>
              <Button
                type='button'
                variant='secondary'
                className='border-stone-300 bg-white text-foreground hover:bg-stone-50'
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                colorTone='primary'
                appearance='filled'
                isLoading={forgotMutation.isPending}
              >
                Send instructions
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
